/**
 * Layer structure, the responsive letterbox, and the pointer parallax.
 *
 * Everything in the game is authored at DESIGN (1920x1080) from
 * `@/pixi/layout`. Nothing downstream of this file should ever look at the real
 * viewport size: the design-space root is scaled to fit and centred, and any
 * viewport that is not 16:9 gets void-coloured bars. That is what lets pile
 * positions, the light and the shadow constants stay simple constants.
 *
 * Layer order, back to front:
 *
 *   background  the room behind the table — gradients, ambient wash
 *   table       felt, rail, pile wells
 *   shadows     card drop shadows, drawn *below* the cards that cast them
 *   cards       the cards themselves
 *   vfx         particles, score popups, flares
 *   overlay     in-canvas HUD elements that must sit above everything
 *
 * Shadows are their own layer rather than children of their cards on purpose:
 * a shadow has to fall on the felt and on the cards *beneath* it, but never on
 * the card casting it, and a card in a pile has to occlude the shadow of the
 * card above it. One flat shadow layer under one flat card layer is the only
 * arrangement that gets that right without per-card z juggling.
 *
 * ## Pointer parallax — see `setParallax`
 *
 * The layer stack is also a depth stack, so it is the cheapest and strongest
 * depth cue this renderer has: sliding each layer by a different fraction of
 * the pointer's distance from centre makes the table read as a physical object
 * seen from a slightly different angle. Three things about it are load-bearing
 * and are explained where they are implemented:
 *
 * - **Shadows are welded to the cards** (`ParallaxRatios.play`). See the note on
 *   that field: the physically-exact separation is 0.05 design units, so 1:1 is
 *   not a compromise, it is the right answer to two decimals.
 * - **Hit-testing still works**, because `toDesignSpace()` subtracts exactly the
 *   offset the play plane was drawn with. See the note on `parallaxOffset`.
 * - **The felt never pulls away from the frame**, because the two full-bleed
 *   layers are overscanned by the exact amount they can travel. See `#COVER`.
 */

import { Container, Graphics, Rectangle } from 'pixi.js';

import { linearFill } from '@/core/fills';

import { palette } from '@/core/tokens';
import { coarsePointer, DESIGN, LIGHT } from '@/pixi/layout';

/** Named layer handles. Other modules attach to these, never to the root. */
export interface Layers {
  /** Design-space root. Children are positioned in DESIGN units. */
  root: Container;
  background: Container;
  table: Container;
  shadows: Container;
  cards: Container;
  vfx: Container;
  overlay: Container;
}

/** Layer names in back-to-front order. */
export const LAYER_ORDER = [
  'background',
  'table',
  'shadows',
  'cards',
  'vfx',
  'overlay',
] as const;

export type LayerName = (typeof LAYER_ORDER)[number];

/** A point in design-space units. */
export interface DesignPoint {
  x: number;
  y: number;
}

/* ------------------------------------------------------------------ *
 * Parallax
 * ------------------------------------------------------------------ */

/**
 * How far each depth band travels, as a fraction of the amplitude.
 *
 * Four bands, not six, because two of the boundaries must not exist.
 */
export interface ParallaxRatios {
  /** The room behind the table. Furthest away, so it barely moves. */
  background: number;
  /** Felt, rail and pile wells — the surface everything rests on. */
  table: number;
  /**
   * Cards, their shadows, and the vfx that annotate them: **one rigid plane**.
   *
   * This is deliberately a single number rather than three. A drop shadow is
   * not an object with its own depth, it is the absence of light on the felt
   * *underneath a specific card*, and `CardShadow.sync()` in `@/pixi/card`
   * derives its whole position from that card and from `LIGHT`. Any parallax
   * differential between the two layers is therefore an offset the light model
   * never authored, and it lands on top of a very small signal: a card resting
   * on the felt sits `CARD_PHYSICS.thickness` (5) units up, so its contact
   * shadow is offset by `5 * SHADOW.offsetScale` = **1.4 design units**. Let the
   * shadows travel at the table's rate instead of the cards' and the differential
   * at full deflection would be 0.7 * 18 = 12.6 units — nine times the entire
   * physical signal, in whatever direction the pointer happens to lie rather
   * than away from the key. Half the time the shadow would appear on the lit
   * side of its own card. That is the failure the brief warns about, and it is
   * worse than no parallax at all.
   *
   * The physically correct differential is also, conveniently, nothing. The
   * felt shader in `@/pixi/table` views the table from `EYE_Z = 1750`, and two
   * planes 5 units apart separate by `5 / 1750` of the camera move: at the 18
   * units the play plane travels, the shadow would lag its card by **0.05
   * design units**, roughly a twentieth of a screen pixel at 1:1. Welding them
   * is not an approximation of the truth, it is the truth rounded to two
   * decimal places — and it makes the illusion unbreakable by tuning.
   *
   * (A card genuinely in the air is a different question — at `lift = 115` the
   * separation would be real. That is a per-card effect and belongs to
   * `card.ts`, which already owns the card-to-shadow relationship; a layer
   * cannot express it, and a card in flight is moving far too fast for anyone
   * to read parallax off it.)
   */
  play: number;
  /** In-canvas HUD. Screen furniture, so it is pinned by default. */
  overlay: number;
}

export interface ParallaxOptions {
  /** Master switch. `false` early-outs before any per-frame work. */
  enabled: boolean;
  /**
   * Peak travel of the play plane in design units, at the left/right edge of
   * the frame. Small on purpose: this is a head moving, not a scene sliding.
   */
  amplitudeX: number;
  /** Peak vertical travel of the play plane. Shorter, because the pointer's
   * vertical range is shorter and a table viewed from above foreshortens. */
  amplitudeY: number;
  ratios: ParallaxRatios;
  /**
   * Rate of the critically damped follower, in rad/s. Lower is a heavier
   * camera head; higher sticks to the pointer. At 11 a flick settles in about
   * 400ms with no overshoot at all — the point is that the eye never catches
   * the motion starting or stopping.
   */
  damping: number;
}

/** What `configureParallax()` accepts: any subset, ratios included. */
export type ParallaxSettings = Partial<Omit<ParallaxOptions, 'ratios'>> & {
  ratios?: Partial<ParallaxRatios>;
};

const PARALLAX_DEFAULTS: ParallaxOptions = {
  enabled: true,
  amplitudeX: 18,
  amplitudeY: 12,
  ratios: { background: 0.16, table: 0.3, play: 1, overlay: 0 },
  damping: 11,
};

/**
 * How much of that amplitude a mouse gets.
 *
 * The effect was tuned for a finger, which only moves the pointer while it is
 * touching the glass — brief, intentional, and mostly during a drag. A mouse
 * moves continuously and idly, and the same amplitude that reads as a head
 * shifting over the table then reads as the board not being nailed down: 18
 * design units is about 24 real pixels of travel on a 1440p screen, applied
 * every time the hand moves. A tester reported exactly that — "the whole
 * dashboard moves slightly when moving the mouse" — as a defect rather than an
 * effect, which is the tell.
 *
 * Scaled rather than switched off: the depth cue is doing real work, and at a
 * third of the travel it still separates the play plane from the felt without
 * ever being the thing you notice. Set to 1 to restore the old feel.
 */
const FINE_POINTER_PARALLAX = 1 / 3;

/** The defaults as this input device should feel them. */
export function parallaxFor(coarse: boolean = coarsePointer()): ParallaxOptions {
  const scale = coarse ? 1 : FINE_POINTER_PARALLAX;
  return {
    ...PARALLAX_DEFAULTS,
    amplitudeX: PARALLAX_DEFAULTS.amplitudeX * scale,
    amplitudeY: PARALLAX_DEFAULTS.amplitudeY * scale,
    ratios: { ...PARALLAX_DEFAULTS.ratios },
  };
}

/**
 * How far the table's light reaches into the letterbox bars, in design units.
 *
 * Generous: this is ambient bounce off a surface, not a hard-edged pool, and a
 * short reach reads as a stripe painted beside the table rather than as light.
 */
const SPILL_REACH = 260;

/**
 * Resting colour and strength of that spill: the bounce the felt throws onto
 * the room around it.
 *
 * Derived from the tokens rather than picked, because a hand-chosen value is
 * exactly the thing that goes stale — the first attempt here was a green bounce
 * written from memory, and this table's felt is deep plum. Mixing the felt
 * toward the key's warm colour is what bounce light actually is.
 */
const SPILL_REST_COLOUR = mixRgb(palette.felt, LIGHT.colour, 0.2);
const SPILL_REST_ALPHA = 0.45;

/** Which ratio each layer takes. The three `play` entries are the weld. */
const PARALLAX_BAND: Readonly<Record<LayerName, keyof ParallaxRatios>> = {
  background: 'background',
  table: 'table',
  shadows: 'play',
  cards: 'play',
  vfx: 'play',
  overlay: 'overlay',
};

/**
 * Layers whose content is a full-bleed backdrop sized to the design rect, and
 * which therefore have to be overscanned before they are allowed to move.
 *
 * `@/pixi/table` draws the felt as a mesh spanning exactly (0,0)-(1920,1080)
 * and writes transparent outside its rounded rect, so translating that layer by
 * even three units would peel the rail away from the frame edge and show a
 * strip of `palette.void` down one side. Scaling it about the design centre by
 * exactly twice its maximum travel closes that gap permanently. The scale is
 * computed once per `configureParallax()` and never touched per frame; at the
 * default tuning it is 1.0067, which moves the outermost pile well by 2.3
 * design units — about one percent of a card's width, static, and invisible.
 */
const COVER: ReadonlySet<LayerName> = new Set<LayerName>(['background', 'table']);

/** Linear blend between two 0xRRGGBB colours. */
function mixRgb(a: number, b: number, t: number): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  return (
    (Math.round(ar + (br - ar) * k) << 16) |
    (Math.round(ag + (bg - ag) * k) << 8) |
    Math.round(ab + (bb - ab) * k)
  );
}

/** Longest step the follower will integrate. Matches the app's frame clamp. */
const MAX_PARALLAX_STEP = 1 / 15;

/**
 * Below this the follower is considered parked: it snaps to the target, drops
 * its velocity, and stops writing transforms. That matters — a container whose
 * position is rewritten every frame keeps its whole subtree's world transforms
 * dirty, so an idle pointer really should cost nothing.
 */
const PARALLAX_SETTLE = 1e-4;
/** Smallest play-plane movement worth rewriting six transforms for. */
const PARALLAX_EPSILON = 0.01;

/** One layer's resolved parallax plan. Built on configure, mutated in place. */
interface LayerPlan {
  readonly container: Container;
  /** Local-space origin, which is the pivot when the layer is overscanned. */
  baseX: number;
  baseY: number;
  /** Signed design units of travel per unit of normalised pointer. */
  travelX: number;
  travelY: number;
}

export class Stage {
  /** Attach this to `app.stage`. Screen space; holds the root and the bars. */
  readonly view: Container;

  /** Design-space root. Scaled and offset by `fit()`. */
  readonly root: Container;

  /**
   * The identity-transform group the card layer sits in. Its filters (the card
   * capture, set by the app from the post chain) are what tell bloom and
   * aberration where the cards are. Draw into `layers.cards`, not into this.
   */
  readonly cardsGroup: Container;

  readonly layers: Layers;

  /** The canvas, needed to convert client coordinates. Set by `createApp`. */
  canvas: HTMLCanvasElement | null = null;

  readonly #letterbox: Graphics;
  readonly #surround: Graphics;
  #scale = 1;
  #offsetX = 0;
  #offsetY = 0;
  #screenWidth: number = DESIGN.width;
  #screenHeight: number = DESIGN.height;
  #shakeX = 0;
  #shakeY = 0;
  #shakeRotation = 0;
  /** Camera punch multiplier on the fit. See {@link setPunch}. */
  #punch = 1;

  /* -------------------------------------------------- parallax state */

  #parallax: ParallaxOptions = parallaxFor();
  /** One entry per layer, allocated once. Nothing here allocates per frame. */
  readonly #plans: LayerPlan[] = [];
  /** Normalised pointer the follower is heading for, each axis in -1..1. */
  #targetX = 0;
  #targetY = 0;
  /** Where the follower actually is, and how fast. */
  #pointerX = 0;
  #pointerY = 0;
  #velocityX = 0;
  #velocityY = 0;
  /** Play-plane offset currently written into the scene graph. */
  #playX = 0;
  #playY = 0;
  /** The play band's resolved travel — the cards layer's plan, in scalars. */
  #playTravelX = 0;
  #playTravelY = 0;
  /** Cheap gate: false when disabled, zero-amplitude, or reduced motion. */
  #parallaxLive = false;
  #reducedMotion = false;
  #motionQuery: MediaQueryList | null = null;

  constructor() {
    this.view = new Container({ label: 'view' });

    this.root = new Container({ label: 'design-root' });
    // Local-space filter area: Pixi multiplies this by the container's world
    // transform, so it tracks the letterbox automatically and never needs
    // recomputing on resize. It also clips the post chain to the design rect,
    // which is exactly what we want — nothing bleeds into the bars.
    this.root.filterArea = new Rectangle(0, 0, DESIGN.width, DESIGN.height);

    // Rotating the shake about the middle of the table rather than its corner.
    // The pivot is constant, so `fit()` and `setShake()` both just write
    // position/rotation and the design-space mapping stays a closed form.
    this.root.pivot.set(DESIGN.width / 2, DESIGN.height / 2);

    const make = (label: LayerName): Container => {
      // `@/motion/choreography` drives depth through zIndex bands (Z.well,
      // Z.pile, Z.flight, Z.hero), so the layers it writes into have to sort.
      // Enabling it everywhere costs nothing when no child ever sets a zIndex:
      // Pixi only re-sorts a container once something marks it dirty.
      const container = new Container({ label, sortableChildren: true });
      this.root.addChild(container);
      return container;
    };

    const background = make('background');
    const table = make('table');
    const shadows = make('shadows');

    // The card layer gets a group of its own, at the identity, so the post chain
    // can be told where the cards are (see `CardShield`). It does not move — the
    // parallax and everything else still drive `layers.cards` — which is what
    // keeps its filter area exactly the root's, and the two targets pixel-aligned.
    this.cardsGroup = new Container({ label: 'cards-group' });
    this.cardsGroup.filterArea = new Rectangle(0, 0, DESIGN.width, DESIGN.height);
    this.root.addChild(this.cardsGroup);
    const cards = new Container({ label: 'cards', sortableChildren: true });
    this.cardsGroup.addChild(cards);

    this.layers = {
      root: this.root,
      background,
      table,
      shadows,
      cards,
      vfx: make('vfx'),
      overlay: make('overlay'),
    };

    // Bars live above the root in screen space, so anything parked off-stage
    // (the discard pile, cards mid-flight) stays hidden even with post off.
    this.#letterbox = new Graphics({ label: 'letterbox' });
    this.#letterbox.eventMode = 'none';

    // Above the bars, additive: the table's light bleeding into the room
    // beside it. Drawn white and tinted per frame — see `setSpill`.
    this.#surround = new Graphics({ label: 'surround', blendMode: 'add' });
    this.#surround.eventMode = 'none';
    this.#surround.tint = SPILL_REST_COLOUR;
    this.#surround.alpha = SPILL_REST_ALPHA;

    this.view.addChild(this.root, this.#letterbox, this.#surround);

    this.#watchReducedMotion();
    this.#planParallax();
  }

  /**
   * Colour and strength of the light spilling from the table into the bars.
   *
   * Driven per frame from whatever is glowing on the table — today the streak
   * aura. Both arguments are eased by the caller, not here: this is a straight
   * write of two properties on an already-drawn shape, which is why the
   * surround can be repainted only on resize.
   *
   * @param colour - 0xRRGGBB.
   * @param amount - 0..1, blended against the resting ambient spill.
   */
  setSpill(colour: number, amount: number): void {
    const k = Math.min(Math.max(Number.isFinite(amount) ? amount : 0, 0), 1);
    this.#surround.tint = k <= 0 ? SPILL_REST_COLOUR : mixRgb(SPILL_REST_COLOUR, colour, k);
    this.#surround.alpha = SPILL_REST_ALPHA + (1 - SPILL_REST_ALPHA) * k;
  }

  /** Uniform scale from design units to screen units. */
  get scale(): number {
    return this.#scale;
  }

  /** Screen-space x of the design rect's top-left corner. */
  get offsetX(): number {
    return this.#offsetX;
  }

  /** Screen-space y of the design rect's top-left corner. */
  get offsetY(): number {
    return this.#offsetY;
  }

  /**
   * The design rect in screen units — useful for aligning a DOM overlay.
   * Reported without shake or parallax, because a HUD pinned to a moving rect
   * reads as a bug rather than as depth.
   */
  get designRect(): Rectangle {
    return new Rectangle(
      this.#offsetX,
      this.#offsetY,
      DESIGN.width * this.#scale,
      DESIGN.height * this.#scale,
    );
  }

  /**
   * Fit the design rect inside the given screen size, preserving aspect.
   *
   * @param width - screen width in CSS pixels.
   * @param height - screen height in CSS pixels.
   */
  /**
   * Re-adopt the design rect after `@/pixi/layout` has switched orientation.
   *
   * Both of these are set once in the constructor because the design rect used
   * to be a compile-time constant. It is now per-orientation, so a device that
   * rotates has to be given the new rect explicitly — otherwise the post chain
   * stays clipped to the old shape and the shake pivots around the old centre.
   * `fit()` reads DESIGN live and needs no help.
   */
  relayout(): void {
    this.root.filterArea = new Rectangle(0, 0, DESIGN.width, DESIGN.height);
    this.cardsGroup.filterArea = new Rectangle(0, 0, DESIGN.width, DESIGN.height);
    this.root.pivot.set(DESIGN.width / 2, DESIGN.height / 2);
  }

  fit(width: number, height: number): void {
    const w = Math.max(1, width);
    const h = Math.max(1, height);

    this.#screenWidth = w;
    this.#screenHeight = h;

    // Contain, not cover: whatever is left over becomes void-coloured bars
    // rather than a cropped table, because losing the outer piles would make
    // the game unplayable. A portrait phone no longer relies on that slack —
    // `@/pixi/layout` gives it a design rect of its own shape — so the bars are
    // now only ever a few pixels of rounding.
    this.#scale = Math.min(w / DESIGN.width, h / DESIGN.height);
    this.#offsetX = Math.round((w - DESIGN.width * this.#scale) * 0.5);
    this.#offsetY = Math.round((h - DESIGN.height * this.#scale) * 0.5);

    this.#applyTransform();
    this.#drawLetterbox(w, h);
  }

  /**
   * Screen shake, in design units, applied to the whole table.
   *
   * This is the sink for `VFX`'s `onShake` callback: it owns the impulse and
   * the decay, this owns the transform. The letterbox bars deliberately do not
   * move — the frame is the frame, the table shakes inside it, and anything the
   * shake pushes past the design rect is covered by the bars.
   */
  setShake(x: number, y: number, rotation = 0): void {
    if (x === this.#shakeX && y === this.#shakeY && rotation === this.#shakeRotation) return;
    this.#shakeX = x;
    this.#shakeY = y;
    this.#shakeRotation = rotation;
    this.#applyTransform();
  }

  /** Current shake offset in design units, and its roll in radians. */
  get shake(): { x: number; y: number; rotation: number } {
    return { x: this.#shakeX, y: this.#shakeY, rotation: this.#shakeRotation };
  }

  /**
   * Camera punch: a multiplier on the fit, pivoted at the table's centre.
   *
   * The sink for `VFX`'s `onPunch` callback — the impulse and its decay live
   * there, the transform lives here, exactly as with {@link setShake}.
   *
   * This is the one escalation channel with no object in it at all. A streak
   * used to be sold by scaling a badge, which is the move that reads as
   * cartoon the moment it overshoots; scaling the *camera* cannot, because
   * there is nothing on screen whose proportions change relative to anything
   * else. The whole table leans in and settles back.
   *
   * The ceiling is 10%, and it is a backstop rather than a target — `VFX` caps
   * its own accumulation at 9% and the loudest single event asks for 7.5%.
   * The letterbox is what makes this affordable: the bars already cover
   * whatever a shake pushes past the design rect, and a punch pushes past it
   * the same way, for a sixth of a second.
   */
  setPunch(scale: number): void {
    const next = Math.min(Math.max(scale, 1), 1.10);
    if (next === this.#punch) return;
    this.#punch = next;
    this.#applyTransform();
  }

  /** Current punch multiplier. 1 at rest. */
  get punch(): number {
    return this.#punch;
  }

  /* ---------------------------------------------------------------- *
   * Parallax
   * ---------------------------------------------------------------- */

  /**
   * Aim the parallax at a pointer, given in DOM client coordinates.
   *
   * Only a target is recorded here; `updateParallax(dt)` does the moving. Call
   * it once per frame from wherever the pointer is already being flushed — this
   * is two divisions and two stores, so calling it on every raw `pointermove`
   * is fine too.
   *
   * ```ts
   * pixi.stage.setParallax(event.clientX, event.clientY);
   * ```
   */
  setParallax(clientX: number, clientY: number): void {
    if (!this.#parallaxLive) return;
    const point = this.toDesignSpace(clientX, clientY);
    this.setParallaxDesign(point.x, point.y);
  }

  /**
   * The same, for a caller that already holds a design-space point — which is
   * the usual case, since anything tracking the pointer has hit-tested with it
   * first. Saves the second `getBoundingClientRect()` per frame.
   *
   * The point is expected in play-plane units (what `toDesignSpace()` returns),
   * so the parallax offset is added back before normalising. Without that the
   * effect would be feeding on its own output — stable, but it would quietly
   * shift the neutral point by a couple of percent for no reason.
   */
  setParallaxDesign(designX: number, designY: number): void {
    if (!this.#parallaxLive) return;
    const halfW = DESIGN.width / 2;
    const halfH = DESIGN.height / 2;
    this.#targetX = clamp((designX + this.#playX - halfW) / halfW, -1, 1);
    this.#targetY = clamp((designY + this.#playY - halfH) / halfH, -1, 1);
  }

  /**
   * The pointer has left. Ease back to dead centre rather than freezing where
   * it was — a table left permanently tilted is a table nobody is looking at.
   */
  releaseParallax(): void {
    this.#targetX = 0;
    this.#targetY = 0;
  }

  /**
   * Advance the camera by `dt` seconds and write the layer offsets.
   *
   * **Must be called once per frame**, unconditionally — the follower has to
   * keep integrating after the pointer stops moving, and it is what returns the
   * table to centre after `releaseParallax()`. Call it *before* hit-testing, so
   * a click is tested against the offset the frame is about to be drawn with.
   */
  updateParallax(dt: number): void {
    if (!this.#parallaxLive) return;

    const step = dt > 0 ? Math.min(dt, MAX_PARALLAX_STEP) : 0;
    if (step > 0) this.#stepFollower(step);

    // Read from the same resolved travel the cards layer is written with, so
    // the number `toDesignSpace()` trusts can never drift from the number the
    // scene graph was drawn with.
    const playX = this.#pointerX * this.#playTravelX;
    const playY = this.#pointerY * this.#playTravelY;

    // Once the follower has parked, stop rewriting transforms entirely. Pixi
    // re-derives a container's whole subtree of world transforms when its own
    // transform is dirtied, so an idle pointer should not be paying for six
    // dirty layers a frame.
    const dx = Math.abs(playX - this.#playX);
    const dy = Math.abs(playY - this.#playY);
    if (dx === 0 && dy === 0) return;

    // A deadband while the camera is moving, but one exact flush at the moment
    // it parks — otherwise the last sub-epsilon sliver would never be drawn and
    // `parallaxOffset` would permanently disagree with the scene graph by a
    // hundredth of a unit.
    const parked = this.#pointerX === this.#targetX && this.#pointerY === this.#targetY;
    if (!parked && dx < PARALLAX_EPSILON && dy < PARALLAX_EPSILON) return;

    this.#playX = playX;
    this.#playY = playY;
    this.#writeParallax(this.#pointerX, this.#pointerY);
  }

  /**
   * The offset, in design units, currently applied to the play plane — cards,
   * their shadows, and the vfx that sit with them.
   *
   * This is the number that keeps clicking honest. A card authored at design
   * position P is drawn at P + this, so `toDesignSpace()` subtracts it and
   * `toScreenSpace()` adds it, making the pair exact inverses at any deflection.
   * Everything downstream — `pilePosition()`, the pile hit regions in `main.ts`
   * — therefore keeps working in untouched design coordinates and never learns
   * that the camera moved.
   */
  get parallaxOffset(): DesignPoint {
    return { x: this.#playX, y: this.#playY };
  }

  /** Whether parallax is doing anything: enabled, non-zero, motion allowed. */
  get parallaxActive(): boolean {
    return this.#parallaxLive;
  }

  /** Current tuning. A copy — mutate it through `configureParallax()`. */
  get parallax(): ParallaxOptions {
    return { ...this.#parallax, ratios: { ...this.#parallax.ratios } };
  }

  /**
   * Retune, or switch the whole effect off.
   *
   * ```ts
   * pixi.stage.configureParallax({ enabled: false });      // off, and free
   * pixi.stage.configureParallax({ amplitudeX: 26 });      // bolder
   * ```
   *
   * Turning it off (or setting either amplitude to zero) returns every layer to
   * its authored position and makes `updateParallax()` a single boolean test.
   */
  configureParallax(options: ParallaxSettings): void {
    this.#parallax = {
      enabled: options.enabled ?? this.#parallax.enabled,
      amplitudeX: Math.max(0, options.amplitudeX ?? this.#parallax.amplitudeX),
      amplitudeY: Math.max(0, options.amplitudeY ?? this.#parallax.amplitudeY),
      damping: Math.max(0.5, options.damping ?? this.#parallax.damping),
      ratios: { ...this.#parallax.ratios, ...options.ratios },
    };
    this.#planParallax();
  }

  /**
   * Convert a DOM pointer position into design-space units.
   *
   * Returns coordinates even when the point is outside the design rect, so
   * callers can decide for themselves whether an out-of-frame drag still
   * counts. Use `contains()` when you need the bounds test.
   */
  toDesignSpace(clientX: number, clientY: number): DesignPoint {
    let screenX = clientX;
    let screenY = clientY;

    if (this.canvas) {
      const rect = this.canvas.getBoundingClientRect();
      // The canvas is normally 1:1 with screen units, but honour any CSS
      // scaling rather than assuming it.
      const sx = this.#screenWidth / Math.max(rect.width, 1);
      const sy = this.#screenHeight / Math.max(rect.height, 1);
      screenX = (clientX - rect.left) * sx;
      screenY = (clientY - rect.top) * sy;
    }

    // Exact inverse of #applyTransform, shake included, so a tap still lands on
    // the pile the player is looking at while the table is still ringing.
    const dx = screenX - (this.#originX() + this.#shakeX * this.#scale);
    const dy = screenY - (this.#originY() + this.#shakeY * this.#scale);
    const cos = Math.cos(-this.#shakeRotation);
    const sin = Math.sin(-this.#shakeRotation);

    // ...and then out of the play plane's parallax, so the result is in the
    // same coordinates `@/pixi/layout` authored the piles in. See
    // `parallaxOffset`.
    return {
      x: (dx * cos - dy * sin) / this.#scale + DESIGN.width / 2 - this.#playX,
      y: (dx * sin + dy * cos) / this.#scale + DESIGN.height / 2 - this.#playY,
    };
  }

  /** Convert design-space units back to screen units. */
  toScreenSpace(designX: number, designY: number): DesignPoint {
    const dx = (designX + this.#playX - DESIGN.width / 2) * this.#scale;
    const dy = (designY + this.#playY - DESIGN.height / 2) * this.#scale;
    const cos = Math.cos(this.#shakeRotation);
    const sin = Math.sin(this.#shakeRotation);

    return {
      x: dx * cos - dy * sin + this.#originX() + this.#shakeX * this.#scale,
      y: dx * sin + dy * cos + this.#originY() + this.#shakeY * this.#scale,
    };
  }

  /** True when a design-space point falls inside the visible design rect. */
  contains(designX: number, designY: number): boolean {
    return designX >= 0 && designY >= 0 && designX <= DESIGN.width && designY <= DESIGN.height;
  }

  destroy(): void {
    this.canvas = null;
    this.#motionQuery?.removeEventListener('change', this.#onMotionPreference);
    this.#motionQuery = null;
    this.#plans.length = 0;
    this.view.destroy({ children: true });
  }

  /** Screen-space position of the design centre, before shake. */
  #originX(): number {
    return this.#offsetX + (DESIGN.width / 2) * this.#scale;
  }

  #originY(): number {
    return this.#offsetY + (DESIGN.height / 2) * this.#scale;
  }

  #applyTransform(): void {
    // The punch multiplies the fit rather than replacing it, so the letterbox
    // and every layout measurement stay in design units and know nothing about
    // it. The pivot is the table's centre, so this pushes in rather than
    // sliding — and because the bars do not move, a punch that overruns the
    // design rect is covered exactly as a shake is.
    const scale = this.#scale * this.#punch;
    this.root.scale.set(scale);
    this.root.rotation = this.#shakeRotation;
    this.root.position.set(
      this.#originX() + this.#shakeX * scale,
      this.#originY() + this.#shakeY * scale,
    );
  }

  /* -------------------------------------------------- parallax internals */

  /**
   * Resolve the tuning into one plan per layer: where the layer's local origin
   * is, how far it travels, and how much it has to be overscanned to keep
   * covering the frame while it does. All of it precomputed, because the
   * per-frame path is allowed to do nothing but multiply and store.
   */
  #planParallax(): void {
    const p = this.#parallax;
    const live =
      p.enabled && !this.#reducedMotion && (p.amplitudeX > 0 || p.amplitudeY > 0) && p.damping > 0;

    this.#plans.length = 0;

    for (const name of LAYER_ORDER) {
      const container = this.layers[name];
      const ratio = live ? p.ratios[PARALLAX_BAND[name]] : 0;
      // Negative: the pointer moving right is the viewer's head moving right,
      // which slides everything in front of them to the left. The scene is
      // being looked around, not dragged.
      const travelX = -p.amplitudeX * ratio;
      const travelY = -p.amplitudeY * ratio;

      let cover = 1;
      if (COVER.has(name) && ratio !== 0) {
        // Twice the travel, because the layer swings both ways about centre.
        cover = Math.max(
          1 + (2 * Math.abs(travelX)) / DESIGN.width,
          1 + (2 * Math.abs(travelY)) / DESIGN.height,
        );
      }

      const baseX = cover === 1 ? 0 : DESIGN.width / 2;
      const baseY = cover === 1 ? 0 : DESIGN.height / 2;
      container.pivot.set(baseX, baseY);
      container.scale.set(cover);

      this.#plans.push({ container, baseX, baseY, travelX, travelY });
      if (name === 'cards') {
        this.#playTravelX = travelX;
        this.#playTravelY = travelY;
      }
    }

    this.#parallaxLive = live;

    if (!live) {
      this.#targetX = 0;
      this.#targetY = 0;
      this.#pointerX = 0;
      this.#pointerY = 0;
      this.#velocityX = 0;
      this.#velocityY = 0;
      this.#playX = 0;
      this.#playY = 0;
    }

    // Always write once, so a retune (or a switch-off) lands immediately
    // instead of waiting for the next frame that happens to clear the epsilon.
    this.#writeParallax(this.#pointerX, this.#pointerY);
  }

  #writeParallax(px: number, py: number): void {
    // Indexed, not `for...of`: this is the per-frame path and it is not allowed
    // to allocate an iterator, however cheap the engine makes one.
    const plans = this.#plans;
    for (let i = 0; i < plans.length; i++) {
      const plan = plans[i];
      plan.container.position.set(plan.baseX + px * plan.travelX, plan.baseY + py * plan.travelY);
    }
  }

  /**
   * A critically damped follower, integrated implicitly so it is stable at any
   * frame rate and can never overshoot.
   *
   * First-order smoothing was the other option and reads as a rubber band —
   * maximum speed the instant the pointer moves. This has mass: it eases out of
   * rest and eases back into it, which is the difference between a camera and a
   * layer being dragged.
   */
  #stepFollower(dt: number): void {
    const w = this.#parallax.damping;
    const f = 1 + 2 * dt * w;
    const oo = w * w;
    const hoo = dt * oo;
    const hhoo = dt * hoo;
    const inv = 1 / (f + hhoo);

    const nextX = (f * this.#pointerX + dt * this.#velocityX + hhoo * this.#targetX) * inv;
    this.#velocityX = (this.#velocityX + hoo * (this.#targetX - this.#pointerX)) * inv;
    this.#pointerX = nextX;

    const nextY = (f * this.#pointerY + dt * this.#velocityY + hhoo * this.#targetY) * inv;
    this.#velocityY = (this.#velocityY + hoo * (this.#targetY - this.#pointerY)) * inv;
    this.#pointerY = nextY;

    // Park it exactly, so the epsilon gate below can go quiet for good.
    if (
      Math.abs(this.#targetX - this.#pointerX) < PARALLAX_SETTLE &&
      Math.abs(this.#velocityX) < PARALLAX_SETTLE
    ) {
      this.#pointerX = this.#targetX;
      this.#velocityX = 0;
    }
    if (
      Math.abs(this.#targetY - this.#pointerY) < PARALLAX_SETTLE &&
      Math.abs(this.#velocityY) < PARALLAX_SETTLE
    ) {
      this.#pointerY = this.#targetY;
      this.#velocityY = 0;
    }
  }

  #onMotionPreference = (event: MediaQueryListEvent): void => {
    this.#reducedMotion = event.matches;
    this.#planParallax();
  };

  /**
   * Reduced motion kills the parallax outright rather than shrinking it. It is
   * the one effect in the scene whose entire content is motion, and it is
   * driven by the pointer, so a scaled-down version is still exactly the thing
   * the preference is asking not to happen.
   *
   * Watched live, not sampled once: the preference is a system toggle and the
   * table should obey it without a reload.
   */
  #watchReducedMotion(): void {
    if (typeof globalThis.matchMedia !== 'function') return;
    const query = globalThis.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion = query.matches;
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', this.#onMotionPreference);
      this.#motionQuery = query;
    }
  }

  #drawLetterbox(screenWidth: number, screenHeight: number): void {
    const g = this.#letterbox;
    g.clear();
    this.#surround.clear();

    const left = this.#offsetX;
    const top = this.#offsetY;
    const right = screenWidth - left - DESIGN.width * this.#scale;
    const bottom = screenHeight - top - DESIGN.height * this.#scale;

    if (left <= 0 && top <= 0 && right <= 0 && bottom <= 0) return;

    // Generous overdraw on the far edges so sub-pixel rounding never leaves a
    // seam between a bar and the edge of the canvas.
    if (left > 0) g.rect(-1, -1, left + 1, screenHeight + 2);
    if (right > 0) g.rect(screenWidth - right, -1, right + 1, screenHeight + 2);
    if (top > 0) g.rect(-1, -1, screenWidth + 2, top + 1);
    if (bottom > 0) g.rect(-1, screenHeight - bottom, screenWidth + 2, bottom + 1);

    g.fill({ color: palette.void, alpha: 1 });

    this.#drawSurround(screenWidth, screenHeight, left, top, right, bottom);
  }

  /**
   * The spill: light from the table bleeding into the bars beside it.
   *
   * Without this the bars are flat `palette.void` and the table's light — and
   * the streak aura on top of it — stops dead at the design rect, which reads
   * as a lit picture pasted onto black rather than as a table standing in a
   * room. The gradient runs bright at the table edge to nothing at the screen
   * edge, which is how a real spill falls off.
   *
   * Drawn once per resize in white and then tinted per frame by
   * {@link setSpill}, because a per-frame redraw of four gradient rects would
   * be a real cost for something that only ever changes colour and intensity.
   */
  #drawSurround(
    screenWidth: number,
    screenHeight: number,
    left: number,
    top: number,
    right: number,
    bottom: number,
  ): void {
    const g = this.#surround;
    const tableW = DESIGN.width * this.#scale;
    const tableH = DESIGN.height * this.#scale;
    // Reach is capped so a very wide window does not wash its whole surround.
    const reach = Math.min(SPILL_REACH * this.#scale, Math.max(screenWidth, screenHeight) * 0.5);

    const band = (
      x: number,
      y: number,
      w: number,
      h: number,
      from: { x: number; y: number },
      to: { x: number; y: number },
    ): void => {
      if (w <= 0 || h <= 0) return;
      // White, then tinted per frame by `setSpill`. Guarded so a node test can
      // still build a Stage — see `@/core/fills`.
      g.rect(x, y, w, h).fill(
        linearFill(from, to, [
          { offset: 0, color: 0xffffff, alpha: 1 },
          { offset: 0.45, color: 0xffffff, alpha: 0.28 },
          { offset: 1, color: 0xffffff, alpha: 0 },
        ]),
      );
    };

    // Each band starts at the table edge and fades away from it.
    if (left > 0) {
      band(left - reach, top, reach, tableH, { x: 1, y: 0 }, { x: 0, y: 0 });
    }
    if (right > 0) {
      band(left + tableW, top, reach, tableH, { x: 0, y: 0 }, { x: 1, y: 0 });
    }
    if (top > 0) {
      band(left, top - reach, tableW, reach, { x: 0, y: 1 }, { x: 0, y: 0 });
    }
    if (bottom > 0) {
      band(left, top + tableH, tableW, reach, { x: 0, y: 0 }, { x: 0, y: 1 });
    }
  }
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function createStage(): Stage {
  return new Stage();
}
